import { Suspense, lazy } from 'react';
import { Route, Switch, Redirect } from 'wouter';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/auth/AuthContext';
import { AppShell } from '@/components/layout/AppShell';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Dashboard } from '@/pages/Dashboard';
import { PreferencesPage } from '@/pages/Preferences';

// Heavy pages get their own chunks. Vite/Rolldown splits the dynamic-import
// boundaries automatically. Pages that pull in big dependencies (Leaflet,
// recharts, qr-scanner, etc) stay out of the initial bundle so the
// dashboard's first paint isn't dragged down.
const SitesPage = lazy(() =>
  import('@/pages/admin/Sites').then((m) => ({ default: m.SitesPage })),
);
const SiteZonesPage = lazy(() =>
  import('@/pages/admin/SiteZones').then((m) => ({ default: m.SiteZonesPage })),
);
const SiteGeofencePage = lazy(() =>
  import('@/pages/admin/SiteGeofence').then((m) => ({ default: m.SiteGeofencePage })),
);
const CheckpointsPage = lazy(() =>
  import('@/pages/admin/Checkpoints').then((m) => ({ default: m.CheckpointsPage })),
);
const UsersPage = lazy(() =>
  import('@/pages/admin/Users').then((m) => ({ default: m.UsersPage })),
);
const SettingsPage = lazy(() =>
  import('@/pages/admin/Settings').then((m) => ({ default: m.SettingsPage })),
);
const AdminCensusPage = lazy(() =>
  import('@/pages/admin/Census').then((m) => ({ default: m.CensusPage })),
);
const CensusImportsPage = lazy(() =>
  import('@/pages/admin/CensusImports').then((m) => ({ default: m.CensusImportsPage })),
);
const ScanPage = lazy(() =>
  import('@/pages/sites/Scan').then((m) => ({ default: m.ScanPage })),
);
const ToursPage = lazy(() =>
  import('@/pages/sites/Tours').then((m) => ({ default: m.ToursPage })),
);
const AlertsPage = lazy(() =>
  import('@/pages/sites/Alerts').then((m) => ({ default: m.AlertsPage })),
);
const RoundingPage = lazy(() =>
  import('@/pages/sites/Rounding').then((m) => ({ default: m.RoundingPage })),
);
const RoundingHistoryPage = lazy(() =>
  import('@/pages/sites/RoundingHistory').then((m) => ({ default: m.RoundingHistoryPage })),
);
const DARPage = lazy(() => import('@/pages/sites/DAR').then((m) => ({ default: m.DARPage })));
const StoragePage = lazy(() =>
  import('@/pages/sites/Storage').then((m) => ({ default: m.StoragePage })),
);
const StorageDischargePage = lazy(() =>
  import('@/pages/sites/StorageDischarge').then((m) => ({ default: m.StorageDischargePage })),
);
const HotBoxPage = lazy(() =>
  import('@/pages/sites/HotBox').then((m) => ({ default: m.HotBoxPage })),
);
const SiteCensusPage = lazy(() =>
  import('@/pages/sites/Census').then((m) => ({ default: m.CensusPage })),
);

export function App() {
  const { isLoading, isError, isGlobalAdmin } = useAuth();

  if (isLoading) return <FullPageSpinner />;

  if (isError) {
    return (
      <div className="flex h-full items-center justify-center bg-background p-6">
        <div className="max-w-md text-center space-y-2">
          <h1 className="text-xl font-black">Unable to load session</h1>
          <p className="text-sm text-muted-foreground">
            We couldn't reach the API. Try refreshing — if it keeps failing, the dev API may be down.
          </p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <AppShell>
        <Suspense fallback={<FullPageSpinner />}>
          <Switch>
            <Route path="/" component={Dashboard} />
            <Route path="/me/preferences" component={PreferencesPage} />
            {isGlobalAdmin && <Route path="/admin/sites" component={SitesPage} />}
            {isGlobalAdmin && (
              <Route path="/admin/sites/:siteId/zones" component={SiteZonesPage} />
            )}
            {isGlobalAdmin && (
              <Route path="/admin/sites/:siteId/geofence" component={SiteGeofencePage} />
            )}
            {isGlobalAdmin && <Route path="/admin/checkpoints" component={CheckpointsPage} />}
            {isGlobalAdmin && <Route path="/admin/users" component={UsersPage} />}
            {isGlobalAdmin && <Route path="/admin/settings" component={SettingsPage} />}
            {isGlobalAdmin && <Route path="/admin/census" component={AdminCensusPage} />}
            {isGlobalAdmin && (
              <Route path="/admin/census/imports/:siteId" component={CensusImportsPage} />
            )}

            {/* Site-scoped field routes — backend gates on site role; frontend just renders */}
            <Route path="/sites/:slug/scan" component={ScanPage} />
            <Route path="/sites/:slug/tours" component={ToursPage} />
            <Route path="/sites/:slug/alerts" component={AlertsPage} />
            <Route path="/sites/:slug/rounding" component={RoundingPage} />
            <Route path="/sites/:slug/rounding/history" component={RoundingHistoryPage} />
            <Route path="/sites/:slug/dar" component={DARPage} />
            <Route path="/sites/:slug/storage/temp" component={HotBoxPage} />
            <Route path="/sites/:slug/storage/discharge/:itemId" component={StorageDischargePage} />
            <Route path="/sites/:slug/storage" component={StoragePage} />
            <Route path="/sites/:slug/census" component={SiteCensusPage} />
            <Route>
              <Redirect to="/" />
            </Route>
          </Switch>
        </Suspense>
      </AppShell>
    </ErrorBoundary>
  );
}

function FullPageSpinner() {
  return (
    <div className="flex h-full items-center justify-center bg-background">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );
}
